import React, { useEffect, useState, useRef, Fragment } from "react";
import { PORTFOLIO as PD } from "./data";
import { useReducedMotion, useIsLive } from "./hooks";

export function HeroBg() {
  const rm = useReducedMotion();
  const [lines, setLines] = useState([]);
  const ref = useRef(null);
  const live = useIsLive(ref);
  useEffect(() => {
    if (rm) {
      setLines([
        { sev: "info", t: "00:00:00", msg: "soc.session.started analyst=hi3ris" },
        { sev: "med",  t: "00:00:04", msg: "auth.failure user=svc_backup" },
        { sev: "high", t: "00:00:09", msg: "lateral.movement host=db-04" },
      ]);
      return;
    }
    if (!live) return;
    const seed = [
      { sev: "info", msg: "soc.session.started analyst=hi3ris" },
      { sev: "low",  msg: "edr.heartbeat ok endpoints=412" },
      { sev: "med",  msg: "auth.failure.spike user=svc_backup count=42" },
      { sev: "high", msg: "lateral.movement.detected host=db-04" },
      { sev: "high", msg: "exfil.candidate bytes=2.1GB dst=185.x.x.x" },
      { sev: "med",  msg: "edr.quarantine pid=4112 sha=ab12…ef" },
      { sev: "low",  msg: "user.geo.anomaly user=alice dist=5400km" },
      { sev: "info", msg: "playbook.run id=PB-2041 status=ok" },
      { sev: "med",  msg: "siem.rule.fired id=R-0931 score=72" },
      { sev: "high", msg: "c2.beacon.detected interval=300s ja3=…" },
      { sev: "info", msg: "ti.feed.refresh shadowserver=1.2k entries" },
      { sev: "low",  msg: "outbound.tls hash=mismatch sni=cdn.example" },
    ];
    let i = 0;
    const start = Date.now();
    const id = setInterval(() => {
      const ms = Date.now() - start;
      const t = new Date(ms).toISOString().substr(14, 5);
      const line = { ...seed[i % seed.length], t: "00:" + t };
      setLines((prev) => [...prev.slice(-9), line]);
      i++;
    }, 1100);
    return () => clearInterval(id);
  }, [rm, live]);
  return (
    <div ref={ref} className="hero-bg" aria-hidden="true">
      <div className="hero-bg-grid" />
      <div className="hero-console">
        <div className="hero-console-h"><span className="hc-dot" /> soc.tail —f</div>
        {lines.map((l, i) => (
          <div key={i} className={"hc-line sev-" + l.sev}>
            <span className="hc-t">{l.t}</span>
            <span className={"hc-sev sev-" + l.sev}>{l.sev.toUpperCase().padEnd(4," ")}</span>
            <span className="hc-msg">{l.msg}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function RegionMap() {
  const rm = useReducedMotion();
  const home = PD.region.find((r) => r.home);
  const others = PD.region.filter((r) => !r.home);
  return (
    <div className="region-wrap" aria-label="Map of West Africa connections" role="img">
      <div className="region-hd">
        <span className="region-hd-l">◉ REGIONAL MESH</span>
        <span className="region-hd-r">{PD.region.length} NODES · LIVE</span>
      </div>
      <svg viewBox="0 0 100 100" className="region-svg" preserveAspectRatio="xMidYMid meet">
        <defs>
          <linearGradient id="rgrad" x1="0" x2="1">
            <stop offset="0" stopColor="var(--c-acc)" stopOpacity="0.05"/>
            <stop offset="1" stopColor="var(--c-acc)" stopOpacity="0.7"/>
          </linearGradient>
          <pattern id="rgrid" width="10" height="10" patternUnits="userSpaceOnUse">
            <path d="M 10 0 L 0 0 0 10" fill="none" stroke="var(--c-rule)" strokeWidth="0.15" opacity="0.6" />
          </pattern>
          <radialGradient id="rhalo" cx="0.5" cy="0.5" r="0.5">
            <stop offset="0" stopColor="var(--c-acc)" stopOpacity="0.35" />
            <stop offset="1" stopColor="var(--c-acc)" stopOpacity="0" />
          </radialGradient>
        </defs>

        <rect width="100" height="100" fill="url(#rgrid)" />

        {/* coastline hint — Gulf of Guinea */}
        <path d="M 0 80 Q 22 78 42 82 T 78 78 L 100 76"
              stroke="var(--c-acc)" strokeWidth="0.2" fill="none" opacity="0.4"
              strokeDasharray="0.6 0.6" />
        <text x="2" y="86" className="region-coast">~ GULF OF GUINEA</text>

        {/* corner brackets */}
        <g stroke="var(--c-acc)" strokeWidth="0.35" fill="none" opacity="0.85">
          <path d="M 1 5 L 1 1 L 5 1" />
          <path d="M 95 1 L 99 1 L 99 5" />
          <path d="M 99 95 L 99 99 L 95 99" />
          <path d="M 5 99 L 1 99 L 1 95" />
        </g>

        {/* halo behind LOMÉ */}
        <circle cx={home.x} cy={home.y} r="14" fill="url(#rhalo)" />

        {/* connection lines */}
        {others.map((r, i) => (
          <line key={r.code}
            x1={home.x} y1={home.y} x2={r.x} y2={r.y}
            stroke="url(#rgrad)" strokeWidth="0.25"
            className={"region-line" + (rm ? " rm" : "")}
            style={{ animationDelay: (i * 80) + "ms" }} />
        ))}

        {/* animated packets converging on LOMÉ */}
        {!rm && others.map((r, i) => (
          <circle key={r.code + "-pkt"} cx={r.x} cy={r.y} r="0.55"
                  fill="var(--c-acc)" className="region-pkt">
            <animate attributeName="cx" from={r.x} to={home.x}
                     dur="2.6s" begin={(i * 0.32) + "s"} repeatCount="indefinite" />
            <animate attributeName="cy" from={r.y} to={home.y}
                     dur="2.6s" begin={(i * 0.32) + "s"} repeatCount="indefinite" />
            <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.15;0.85;1"
                     dur="2.6s" begin={(i * 0.32) + "s"} repeatCount="indefinite" />
            <animate attributeName="r" values="0.55;0.9;0.55" keyTimes="0;0.5;1"
                     dur="2.6s" begin={(i * 0.32) + "s"} repeatCount="indefinite" />
          </circle>
        ))}

        {/* country dots + 2-letter codes */}
        {PD.region.map((r) => (
          <g key={r.code} transform={`translate(${r.x} ${r.y})`}>
            <circle r={r.home ? 1.7 : 0.75} className={r.home ? "region-home" : "region-dot"} />
            {r.home && <>
              <circle r="3" className="region-pulse" />
              <circle r="3" className="region-pulse" style={{ animationDelay: "0.7s" }} />
              <circle r="3" className="region-pulse" style={{ animationDelay: "1.4s" }} />
            </>}
            {!r.home && <text x="1.2" y="1.4" className="region-clabel">{r.code}</text>}
          </g>
        ))}

        {/* LOMÉ label with backing */}
        <g transform={`translate(${home.x + 2.5} ${home.y - 2.5})`}>
          <rect x="-0.3" y="-2.4" width="13" height="3" fill="var(--c-acc)" />
          <text x="0.3" y="0" className="region-label-on-acc">LOMÉ · TG</text>
        </g>
      </svg>
      <div className="region-ft">
        <span>LAT 6.13°N</span>
        <span>LON 1.22°E</span>
        <span className="region-ft-live"><span className="region-ft-dot" /> UPLINK</span>
      </div>
    </div>
  );
}

export function HeroH1({ t }) {
  const rm = useReducedMotion();
  const l1 = t(PD.i18n.h1_l1);
  const strike = t(PD.i18n.h1_strike);
  const mid = t(PD.i18n.h1_mid);
  const l3 = t(PD.i18n.h1_l3);

  const [typed, setTyped] = useState(rm ? l1 : "");
  useEffect(() => {
    if (rm) { setTyped(l1); return; }
    setTyped("");
    let i = 0;
    const id = setInterval(() => {
      i++;
      setTyped(l1.slice(0, i));
      if (i >= l1.length) clearInterval(id);
    }, 70);
    return () => clearInterval(id);
  }, [l1, rm]);
  return (
    <h1 className="d3-h1" aria-label={`${l1} ${strike.replace(/_/g, " ").trim()} ${mid} ${l3.replace(/\n/g, " ")}`}>
      <span className="d3-h1-l1" aria-hidden="true">{typed}<span className="d3-h1-caret" aria-hidden="true">▌</span></span>
      <span className="d3-h1-l2" aria-hidden="true">
        <span className="d3-h1-strike">{strike}</span>
        <span className="d3-h1-mid">{mid}</span>
      </span>
      <span className="d3-h1-l3" aria-hidden="true">{l3.split("\n").map((s,i) => <Fragment key={i}>{s}{i===0 && <br/>}</Fragment>)}</span>
    </h1>
  );
}

export function SchematicAnimated({ t }) {
  const rm = useReducedMotion();
  const ref = useRef(null);
  const live = useIsLive(ref);
  const [intercepted, setIntercepted] = useState(0x3F);
  useEffect(() => {
    if (rm || !live) return;
    const id = setInterval(
      () => setIntercepted((n) => n + Math.floor(Math.random() * 3) + 1),
      3400
    );
    return () => clearInterval(id);
  }, [rm, live]);
  const counterHex = "0x" + intercepted.toString(16).toUpperCase();
  return (
    <div ref={ref} className="d3-wire" aria-hidden="true">
      <div className="d3-node">
        <div className="d3-node-ic">▲</div>
        <div className="d3-node-l">{t(PD.i18n.schematic_client)}</div>
      </div>
      <div className="d3-line">
        <div className="d3-line-bar" />
        {!rm && <>
          {/* forward traffic: client → target */}
          <span className="d3-pkt d3-pkt-fwd d3-pkt-f1">TCP SYN</span>
          <span className="d3-pkt d3-pkt-fwd d3-pkt-f2">TLS HELLO</span>
          <span className="d3-pkt d3-pkt-fwd d3-pkt-f3">GET /api</span>
          {/* reverse traffic: target → client */}
          <span className="d3-pkt d3-pkt-rev d3-pkt-r1">200 OK</span>
          <span className="d3-pkt d3-pkt-rev d3-pkt-r2">JWT</span>
          <span className="d3-line-flash" aria-hidden="true" />
        </>}
        <div className="d3-line-tap">
          <div className="d3-tap-box">{PD.i18n.schematic_mitm}</div>
          <div className="d3-tap-down" aria-hidden="true">↓</div>
          <div className="d3-tap-counter">
            <span className="d3-tap-counter-l">INTERCEPTED</span>
            <span className="d3-tap-counter-v">{counterHex}</span>
          </div>
        </div>
      </div>
      <div className="d3-node">
        <div className="d3-node-ic">■</div>
        <div className="d3-node-l">{t(PD.i18n.schematic_target)}</div>
      </div>
    </div>
  );
}
